import { formatDistanceToNow } from 'date-fns'
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Database,
  FileText,
  Layers,
  Loader2,
  RotateCw,
  SkipForward,
  Sparkles,
  TableProperties,
  XCircle,
} from 'lucide-react'
import { useMemo } from 'react'
import type { LakeDocument } from '@/services/api/lake'
import { formatDocumentType, formatFileType } from '@/utils/document-labels'
import { LakeDocumentPreview } from '../LakeDocumentPreview'
import {
  BulletList,
  DetailCard,
  EntityChipTone,
  MetaField,
  OutlineBadge,
  PageTitle,
  SectionHeading,
} from './sections'
import { formatBytes } from './utils'

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Render seconds as ``mm:ss`` (or ``h:mm:ss`` past one hour). Used for the
 *  audio document detail metadata strip. */
function formatAudioDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(ss)}`
  return `${pad(m)}:${pad(ss)}`
}

/** Safely convert a value to a renderable string — guards against objects returned by LLM. */
function toRenderable(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

/** Strip markdown code fences (```json ... ```) and surrounding whitespace —
 *  some LLM outputs wrap their structured response in fences even when the
 *  schema asks for a plain string. */
function stripCodeFence(raw: string): string {
  let s = raw.trim()
  // Leading fence: ``` or ```json (case-insensitive language tag)
  s = s.replace(/^```(?:[a-zA-Z0-9_-]+)?\s*\n?/, '')
  // Trailing fence
  s = s.replace(/\n?```\s*$/, '')
  return s.trim()
}

/** Heuristic: does this string look like it's trying to be a JSON object? */
function looksLikeJsonObject(s: string): boolean {
  const stripped = stripCodeFence(s)
  return stripped.startsWith('{')
}

/** Attempt to recover a JSON object from a string that may be malformed —
 *  truncated, missing closing braces/quotes, wrapped in markdown fences,
 *  or containing unescaped newlines. Used when the LLM packed structured
 *  output into a `str` field but the resulting string isn't strict JSON. */
function tryParseLenient(raw: string): Record<string, unknown> | null {
  const cleaned = stripCodeFence(raw)

  // 1. Strict parse first — fastest, handles 99% of valid JSON.
  try {
    const v = JSON.parse(cleaned)
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return v as Record<string, unknown>
    }
  } catch {
    /* fall through */
  }

  // 2. Try repairing the most common issues: trailing comma, unterminated
  //    string, missing closing brace. Naive but cheap.
  const repaired = cleaned.replace(/,\s*([}\]])/g, '$1') // strip trailing commas before }/]
  const candidates = [
    repaired,
    `${repaired}"}`, // unterminated string + missing brace
    `${repaired}"`, // unterminated string only
    `${repaired}}`, // missing brace only
    `${repaired}"]}`,
    `${repaired}]}`,
    `${repaired}}]}`,
  ]
  for (const candidate of candidates) {
    try {
      const v = JSON.parse(candidate)
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        return v as Record<string, unknown>
      }
    } catch {
      /* keep trying */
    }
  }

  // 3. Regex fallback — pluck the obvious fields out of the raw text.
  const out: Record<string, unknown> = {}
  const docTypeMatch = cleaned.match(/"document_type"\s*:\s*"([^"]+)"/)
  if (docTypeMatch) out.document_type = docTypeMatch[1]
  const fileTypeMatch = cleaned.match(/"file_type"\s*:\s*"([^"]+)"/)
  if (fileTypeMatch) out.file_type = fileTypeMatch[1]
  const confMatch = cleaned.match(/"confidence"\s*:\s*([\d.]+)/)
  if (confMatch) {
    const n = Number(confMatch[1])
    if (!Number.isNaN(n)) out.confidence = n
  }
  // Summary may contain commas, periods, parens — capture greedily until the
  // next quoted key or end-of-input.
  const summaryMatch = cleaned.match(/"summary"\s*:\s*"((?:[^"\\]|\\.)*)/)
  if (summaryMatch) {
    out.summary = summaryMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ')
  }
  const extractionSummaryMatch = cleaned.match(/"extraction_summary"\s*:\s*"((?:[^"\\]|\\.)*)/)
  if (extractionSummaryMatch) {
    out.extraction_summary = extractionSummaryMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ')
  }
  // Arrays of strings: potential_entities, key_data_points, potential_relationships
  for (const key of ['potential_entities', 'key_data_points', 'potential_relationships']) {
    const re = new RegExp(`"${key}"\\s*:\\s*\\[([\\s\\S]*?)\\]`)
    const m = cleaned.match(re)
    if (m) {
      const items = Array.from(m[1].matchAll(/"((?:[^"\\]|\\.)*)"/g)).map(x =>
        x[1].replace(/\\"/g, '"')
      )
      if (items.length > 0) out[key] = items
    }
  }
  // sample_entities is an object of {EntityType: [items]} — pull each group.
  // The string may be truncated mid-object, so don't require a closing brace.
  const sampleStart = cleaned.search(/"sample_entities"\s*:\s*\{/)
  if (sampleStart !== -1) {
    const openIdx = cleaned.indexOf('{', sampleStart)
    const block = cleaned.slice(openIdx + 1)
    const groups: Record<string, string[]> = {}
    const groupRe = /"([A-Za-z0-9_]+)"\s*:\s*\[([\s\S]*?)\]/g
    let g: RegExpExecArray | null
    g = groupRe.exec(block)
    while (g !== null) {
      // Stop if we've walked past sample_entities into a sibling top-level key.
      if (g[1] === 'key_data_points' || g[1] === 'potential_relationships') break
      const items = Array.from(g[2].matchAll(/"((?:[^"\\]|\\.)*)"/g)).map(x =>
        x[1].replace(/\\"/g, '"')
      )
      if (items.length > 0) groups[g[1]] = items
      g = groupRe.exec(block)
    }
    if (Object.keys(groups).length > 0) out.sample_entities = groups
  }
  return Object.keys(out).length > 0 ? out : null
}

/** Some pipelines stuff the entire analysis payload (document_type, confidence,
 *  summary, …) into the `summary` field as a JSON string. Recover the
 *  individual fields so the UI shows them properly instead of rendering
 *  raw JSON to the user. */
function resolveAnalysis(analysisResult: any | null | undefined): {
  fileType: string
  documentType: string
  confidence: number | undefined
  summary: string
  keyFindings: unknown[]
} {
  const fallback = {
    fileType: 'unknown',
    documentType: 'unknown',
    confidence: undefined as number | undefined,
    summary: '',
    keyFindings: [] as unknown[],
  }
  if (!analysisResult) return fallback

  const rawSummary = analysisResult.summary
  const looksLikeJson = typeof rawSummary === 'string' && looksLikeJsonObject(rawSummary)
  const parsed = looksLikeJson ? tryParseLenient(rawSummary) : null

  const documentType =
    (analysisResult.document_type && analysisResult.document_type !== 'unknown'
      ? analysisResult.document_type
      : null) ??
    (typeof parsed?.document_type === 'string' ? (parsed.document_type as string) : null) ??
    'unknown'

  const fileType =
    analysisResult.file_type ??
    (typeof parsed?.file_type === 'string' ? (parsed.file_type as string) : null) ??
    'unknown'

  const confidence =
    typeof analysisResult.confidence === 'number'
      ? analysisResult.confidence
      : typeof parsed?.confidence === 'number'
        ? (parsed.confidence as number)
        : undefined

  // When the raw summary looks like JSON, always prefer parsed.summary —
  // and if we couldn't extract one, drop the field rather than render the
  // JSON blob as prose to the user.
  let summary: string
  if (looksLikeJson) {
    summary = typeof parsed?.summary === 'string' ? (parsed.summary as string) : ''
  } else {
    summary = typeof rawSummary === 'string' ? rawSummary : ''
  }

  const keyFindings = Array.isArray(analysisResult.key_findings)
    ? analysisResult.key_findings
    : Array.isArray(parsed?.key_findings)
      ? (parsed.key_findings as unknown[])
      : []

  return { fileType, documentType, confidence, summary, keyFindings }
}

// ─── Stepper ─────────────────────────────────────────────────────────────────

function IndexationStepper({
  steps,
}: {
  steps: Array<{ icon: React.ElementType; cls: string; label: string }>
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'wrap',
      }}
    >
      {steps.map((step, i, arr) => {
        const Icon = step.icon
        return (
          <div key={`${step.label}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon className={step.cls} style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: 11, color: 'var(--foreground)' }}>{step.label}</span>
            </div>
            {i < arr.length - 1 && (
              <ArrowRight
                size={12}
                style={{ color: 'var(--muted-foreground)', opacity: 0.5, margin: '0 4px' }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface OverviewTabProps {
  document: LakeDocument
  reprocessing: boolean
  onReprocess: () => Promise<void>
}

export function OverviewTab({ document, reprocessing, onReprocess }: OverviewTabProps) {
  const analysisResult = document.analysis_result
  const { fileType, documentType, confidence, summary, keyFindings } = useMemo(
    () => resolveAnalysis(analysisResult),
    [analysisResult]
  )
  const pdfAnalysis = analysisResult?.pdf_analysis
  const csvAnalysis = analysisResult?.csv_analysis
  const extractionPreview = analysisResult?.content_extraction_preview
  const pipelineDisplayName = analysisResult?.pipeline_display_name
  const estimatedTime = analysisResult?.estimated_processing_time

  // If the backend stored raw JSON as extraction_summary (fallback path),
  // recover the structured fields so the preview renders correctly.
  const resolvedPreview = useMemo(() => {
    if (!extractionPreview) return null
    const raw = extractionPreview.extraction_summary
    if (typeof raw !== 'string' || !looksLikeJsonObject(raw)) {
      return extractionPreview
    }
    const parsed = tryParseLenient(raw)
    if (!parsed) {
      // Couldn't recover anything — at minimum, never render the raw JSON blob.
      return { ...extractionPreview, extraction_summary: '' }
    }
    const parsedExtraction =
      typeof parsed.extraction_summary === 'string' ? (parsed.extraction_summary as string) : ''
    const parsedEntities = Array.isArray(parsed.potential_entities)
      ? (parsed.potential_entities as unknown[])
      : []
    const parsedSample =
      parsed.sample_entities && typeof parsed.sample_entities === 'object'
        ? (parsed.sample_entities as Record<string, unknown>)
        : {}
    const parsedDataPoints = Array.isArray(parsed.key_data_points)
      ? (parsed.key_data_points as unknown[])
      : []
    const parsedRels = Array.isArray(parsed.potential_relationships)
      ? (parsed.potential_relationships as unknown[])
      : []
    return {
      ...extractionPreview,
      extraction_summary: parsedExtraction,
      potential_entities: extractionPreview.potential_entities?.length
        ? extractionPreview.potential_entities
        : parsedEntities,
      sample_entities: Object.keys(extractionPreview.sample_entities || {}).length
        ? extractionPreview.sample_entities
        : parsedSample,
      key_data_points: extractionPreview.key_data_points?.length
        ? extractionPreview.key_data_points
        : parsedDataPoints,
      potential_relationships: extractionPreview.potential_relationships?.length
        ? extractionPreview.potential_relationships
        : parsedRels,
      confidence:
        typeof parsed.confidence === 'number' ? parsed.confidence : extractionPreview.confidence,
    }
  }, [extractionPreview])

  // Build the stepper from the two lifecycle fields the pipeline maintains:
  // the analysis probe (analysis_status) then indexing (indexing_status).
  // Reindex re-runs the whole flow; the terminal "Searchable" state is
  // reached once indexing_status === 'indexed'.
  const indexingStatus = (document.indexing_status ?? 'pending') as
    | 'pending'
    | 'processing'
    | 'indexed'
    | 'failed'
    | 'skipped'
  const analysisStatus = document.analysis_status as
    | 'analyzing'
    | 'completed'
    | 'failed'
    | 'skipped'
    | null

  const stepDoneCls = 'text-success'
  const stepActiveCls = 'text-info animate-spin'
  const stepMuteCls = 'text-muted-foreground'
  const stepErrorCls = 'text-destructive'
  const stepSkipCls = 'text-warning'

  type Step = { icon: React.ElementType; cls: string; label: string }
  const indexingSteps: Step[] = [{ icon: CheckCircle2, cls: stepDoneCls, label: 'Uploaded' }]

  // Analysis stage.
  if (analysisStatus === 'analyzing') {
    indexingSteps.push({ icon: Loader2, cls: stepActiveCls, label: 'Analyzing' })
  } else if (analysisStatus === 'completed') {
    indexingSteps.push({ icon: CheckCircle2, cls: stepDoneCls, label: 'Analyzed' })
  } else if (analysisStatus === 'failed') {
    indexingSteps.push({ icon: XCircle, cls: stepErrorCls, label: 'Analysis Failed' })
  } else if (analysisStatus === 'skipped') {
    indexingSteps.push({ icon: SkipForward, cls: stepSkipCls, label: 'Analysis Skipped' })
  } else {
    indexingSteps.push({ icon: Circle, cls: stepMuteCls, label: 'Analysis' })
  }

  // Indexing stage + terminal "Searchable".
  if (indexingStatus === 'pending') {
    indexingSteps.push({ icon: Circle, cls: stepMuteCls, label: 'Queued' })
    indexingSteps.push({ icon: Circle, cls: stepMuteCls, label: 'Searchable' })
  } else if (indexingStatus === 'processing') {
    indexingSteps.push({ icon: Loader2, cls: stepActiveCls, label: 'Indexing…' })
    indexingSteps.push({ icon: Circle, cls: stepMuteCls, label: 'Searchable' })
  } else if (indexingStatus === 'indexed') {
    indexingSteps.push({ icon: CheckCircle2, cls: stepDoneCls, label: 'Indexed' })
    indexingSteps.push({ icon: CheckCircle2, cls: stepDoneCls, label: 'Searchable' })
  } else if (indexingStatus === 'failed') {
    indexingSteps.push({
      icon: XCircle,
      cls: 'text-destructive',
      label: 'Indexing Failed',
    })
  } else if (indexingStatus === 'skipped') {
    indexingSteps.push({
      icon: SkipForward,
      cls: 'text-warning',
      label: 'Indexing Skipped',
    })
  }

  // Disable the reindex action while any stage is in flight.
  const isReindexing =
    reprocessing ||
    analysisStatus === 'analyzing' ||
    indexingStatus === 'pending' ||
    indexingStatus === 'processing'

  return (
    <div
      className="max-md:!px-4"
      style={{
        padding: '20px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <PageTitle>Overview</PageTitle>

      {/* ── METADATA ── */}
      <DetailCard>
        <SectionHeading icon={<Database size={14} />} label="Metadata" />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 16,
          }}
        >
          <MetaField label="Size" value={formatBytes(document.file_size)} />
          <MetaField
            label="Uploaded"
            value={formatDistanceToNow(new Date(document.created_at), { addSuffix: true })}
          />
          {/* Audio-specific metadata. Populated by the transcribe_audio step
              for documents routed through the audio pipeline; absent for
              text / image documents. */}
          {document.audio_duration_seconds != null && (
            <MetaField
              label="Duration"
              value={formatAudioDuration(document.audio_duration_seconds)}
            />
          )}
          {document.detected_language && (
            <MetaField label="Language" value={document.detected_language} />
          )}
          {document.speaker_count != null && (
            <MetaField label="Speakers" value={String(document.speaker_count)} />
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <MetaField label="S3 Key" value={document.s3_key} mono />
          </div>
        </div>
      </DetailCard>

      {/* Audio playback for audio docs lives in ``LakeDocumentPreview``
          (rendered elsewhere in the detail layout). Adding a second
          ``<audio>`` here would require duplicating the authenticated
          blob-URL fetch — the API's /files/{s3_key} endpoint needs a
          bearer header that browsers don't send on ``<audio src>``. The
          metadata strip above already surfaces duration / language /
          speaker_count for audio. */}

      {/* ── INDEXATION STATUS ── */}
      <DetailCard>
        <SectionHeading icon={<Layers size={14} />} label="Indexation Status" />
        <IndexationStepper steps={indexingSteps} />

        {indexingStatus !== 'skipped' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              type="button"
              onClick={() => onReprocess()}
              disabled={isReindexing}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 11px',
                borderRadius: '8px',
                background: 'var(--card)',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
                fontFamily: 'inherit',
                fontSize: 11,
                fontWeight: 500,
                cursor: isReindexing ? 'not-allowed' : 'pointer',
                opacity: isReindexing ? 0.6 : 1,
              }}
            >
              {isReindexing ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RotateCw size={11} />
              )}
              {isReindexing
                ? 'Reindexing…'
                : indexingStatus === 'failed'
                  ? 'Retry Indexing'
                  : 'Reindex'}
            </button>
          </div>
        )}

        {(document.indexing_error || (document.analysis_error && document.status === 'failed')) && (
          <div
            style={{
              padding: '8px 11px',
              borderRadius: '8px',
              background: 'var(--destructive-surface)',
              border: '1px solid var(--destructive)',
              color: 'var(--destructive)',
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {document.indexing_error || document.analysis_error}
          </div>
        )}

        {document.status === 'processed' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 8,
            }}
          >
            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '8px 10px',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--muted-foreground)',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: 3,
                }}
              >
                Chunks
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--foreground)',
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {document.chunks_count ?? '—'}
              </div>
            </div>
          </div>
        )}
      </DetailCard>

      {/* ── AI ANALYSIS ── */}
      {document.analysis_status && (
        <DetailCard>
          <SectionHeading
            icon={<Sparkles size={14} style={{ color: 'var(--primary)' }} />}
            label="AI Analysis"
          />

          {document.analysis_status === 'analyzing' && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--muted-foreground)',
                fontStyle: 'italic',
              }}
            >
              Analysis in progress…
            </div>
          )}

          {document.analysis_status === 'completed' && analysisResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 16,
                }}
              >
                <MetaField
                  label="File Format"
                  value={<OutlineBadge tone="neutral">{formatFileType(fileType)}</OutlineBadge>}
                />
                <MetaField
                  label="Document Type"
                  value={
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <OutlineBadge tone="accent">{formatDocumentType(documentType)}</OutlineBadge>
                      {confidence !== undefined && (
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--muted-foreground)',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          {Math.round(confidence * 100)}% confidence
                        </span>
                      )}
                    </div>
                  }
                />
              </div>

              {pipelineDisplayName && (
                <MetaField
                  label="Pipeline"
                  value={pipelineDisplayName}
                  sub={estimatedTime ? `est. ${estimatedTime}` : undefined}
                />
              )}

              {summary && (
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      color: 'var(--muted-foreground)',
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    Summary
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12.5,
                      lineHeight: 1.6,
                      color: 'var(--foreground)',
                    }}
                  >
                    {summary}
                  </p>
                </div>
              )}

              {keyFindings.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      color: 'var(--muted-foreground)',
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    Key Findings
                  </div>
                  <BulletList items={keyFindings.map(f => toRenderable(f))} />
                </div>
              )}

              {pdfAnalysis && (
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      color: 'var(--muted-foreground)',
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    PDF Statistics
                  </div>
                  <div
                    className="max-md:!grid-cols-2"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                      gap: 8,
                    }}
                  >
                    {[
                      { label: 'Pages', value: pdfAnalysis.page_count ?? 0 },
                      { label: 'Tables', value: pdfAnalysis.table_count ?? 0 },
                      { label: 'Images', value: pdfAnalysis.image_count ?? 0 },
                      { label: 'Scanned', value: pdfAnalysis.is_scanned ? 'Yes' : 'No' },
                    ].map(stat => (
                      <div
                        key={stat.label}
                        style={{
                          background: 'var(--card)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '8px 10px',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--muted-foreground)',
                            fontWeight: 600,
                            letterSpacing: 1.2,
                            textTransform: 'uppercase',
                            marginBottom: 3,
                          }}
                        >
                          {stat.label}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--foreground)',
                            fontFamily: 'var(--font-mono)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {csvAnalysis && (
                <div
                  style={{
                    paddingTop: 14,
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <SectionHeading
                    icon={<TableProperties size={13} style={{ color: 'var(--primary)' }} />}
                    label="CSV Details"
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 8,
                    }}
                  >
                    {csvAnalysis.row_count != null && (
                      <CsvStat label="Rows" value={csvAnalysis.row_count.toLocaleString()} />
                    )}
                    {csvAnalysis.columns?.length != null && (
                      <CsvStat label="Columns" value={csvAnalysis.columns.length} />
                    )}
                    {csvAnalysis.detected_delimiter != null && (
                      <CsvStat
                        label="Delimiter"
                        value={
                          csvAnalysis.detected_delimiter === '\t'
                            ? 'TAB'
                            : csvAnalysis.detected_delimiter === ','
                              ? 'Comma (,)'
                              : csvAnalysis.detected_delimiter === ';'
                                ? 'Semicolon (;)'
                                : csvAnalysis.detected_delimiter === '|'
                                  ? 'Pipe (|)'
                                  : JSON.stringify(csvAnalysis.detected_delimiter)
                        }
                        mono
                      />
                    )}
                    {csvAnalysis.data_pattern && (
                      <CsvStat label="Pattern" value={csvAnalysis.data_pattern} />
                    )}
                  </div>
                  {csvAnalysis.possible_entities && csvAnalysis.possible_entities.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          letterSpacing: 1.2,
                          textTransform: 'uppercase',
                          color: 'var(--muted-foreground)',
                          fontWeight: 600,
                          marginBottom: 6,
                        }}
                      >
                        Possible Entities
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {csvAnalysis.possible_entities.map((e: string) => (
                          <OutlineBadge key={e} tone="neutral">
                            {e}
                          </OutlineBadge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {document.analysis_status === 'failed' && (
            <div style={{ fontSize: 12, color: 'var(--destructive)' }}>Analysis failed</div>
          )}
        </DetailCard>
      )}

      {/* ── EXTRACTION PREVIEW ── */}
      {document.analysis_status === 'completed' && resolvedPreview && (
        <DetailCard>
          <SectionHeading
            icon={<Sparkles size={13} style={{ color: 'var(--primary)' }} />}
            label="Extraction Preview"
            count={
              resolvedPreview.confidence !== undefined
                ? `${Math.round(resolvedPreview.confidence * 100)}%`
                : undefined
            }
          />

          {resolvedPreview.extraction_summary && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  color: 'var(--muted-foreground)',
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                Summary
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: 'var(--foreground)',
                }}
              >
                {resolvedPreview.extraction_summary}
              </p>
            </div>
          )}

          {resolvedPreview.key_data_points && resolvedPreview.key_data_points.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  color: 'var(--muted-foreground)',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Key Data Points
              </div>
              <BulletList
                items={resolvedPreview.key_data_points.map((p: unknown) => toRenderable(p))}
              />
            </div>
          )}

          {resolvedPreview.sample_entities &&
            Object.keys(resolvedPreview.sample_entities).length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: 'var(--muted-foreground)',
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  Sample Entities
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.entries(resolvedPreview.sample_entities).map(
                    ([entityType, entities]: [string, any]) => {
                      const tone = EntityChipTone(entityType)
                      return (
                        <div key={entityType}>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: 'var(--muted-foreground)',
                              marginBottom: 4,
                            }}
                          >
                            {entityType}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {Array.isArray(entities) &&
                              entities.slice(0, 8).map((entity: unknown, i: number) => (
                                <span
                                  key={i}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '2px 9px',
                                    borderRadius: '9999px',
                                    background: tone.bg,
                                    color: tone.color,
                                    border: `1px solid ${tone.border}`,
                                    fontSize: 11,
                                    fontWeight: 500,
                                  }}
                                >
                                  {toRenderable(entity)}
                                </span>
                              ))}
                          </div>
                        </div>
                      )
                    }
                  )}
                </div>
              </div>
            )}

          {resolvedPreview.potential_relationships &&
            resolvedPreview.potential_relationships.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: 'var(--muted-foreground)',
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  Potential Relationships
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {resolvedPreview.potential_relationships.map((rel: unknown, i: number) => (
                    <OutlineBadge key={i} tone="neutral">
                      {toRenderable(rel)}
                    </OutlineBadge>
                  ))}
                </div>
              </div>
            )}
        </DetailCard>
      )}

      {/* ── FILE PREVIEW ── */}
      <DetailCard>
        <SectionHeading icon={<FileText size={14} />} label="File Preview" />
        <LakeDocumentPreview document={document} />
      </DetailCard>
    </div>
  )
}

function CsvStat({
  label,
  value,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: 'var(--muted-foreground)',
          fontWeight: 600,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--foreground)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  )
}
